import { baseUrl } from "util/APIBaseUrl";
import postRequest from "util/APIHelperPost";
import getRequest from "util/APIHelperGet";

export interface ReferralRockMember {
  id: string;
  referralURL: string;
  referralCode: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  status: string;
  createdAt: string;
}

export interface ReferralRockEnrollResponse {
  message: string;
  data: ReferralRockMember;
}

export interface ReferralRockReferral {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  externalIdentifier: string;
  phoneNumber: string;
  amount: number;
  amountFormatted: string;
  preferredContact: string;
  createDate: string;
  updateDate: string;
  source: string;
  programId: string;
  programName: string;
  programTitle: string;
  referringMemberId: string;
  referringMemberName: string;
  memberEmail: string;
  memberReferralCode: string;
  memberExternalIdentifier: string;
  approvedDate: string | null;
  qualifiedDate: string | null;
  status: string;
  companyName: string;
  note: string;
  publicNote: string;
  customOption1Name: string;
  customOption2Name: string;
  customText1Name: string;
  customText2Name: string;
  customText3Name: string;
  customOption1Value: string;
  customOption2Value: string;
  customText1Value: string;
  customText2Value: string;
  customText3Value: string;
  conversionNote: string;
  conversionIPAddress: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  browserReferrerUrl: string;
  IPAddressSource: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  recruiterSourceId: string | null;
  recruiterSourceName: string;
  recruiterSourceEmail: string;
  recruiterAssignedId: string | null;
  recruiterAssignedName: string;
  recruiterAssignedEmail: string;
}

export interface ReferralRockStats {
  memberViews: number;
  memberShares: number;
  referralsCount: number;
  lastShare: string | null;
  referralsApproved: number;
  referralsQualified: number;
  referralsPending: number;
  referralsViews: number;
  referralsApprovedAmount: number;
  rewardsPendingAmount: number;
  rewardsIssuedAmount: number;
  rewardAmountTotal: number;
  rewardsCount: number;
  activatedDate: string | null;
  lastActiveDate: string | null;
}

export interface ReferralRockPagination {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

export interface ReferralRockReferralsResponse {
  pagination: ReferralRockPagination;
  stats: ReferralRockStats;
  referrals: ReferralRockReferral[];
}

export interface ReferralStats {
  invitesSent: number;
  invitesOpened: number;
  referralLinkOpened: number;
  referralLinkUniqueVisitors: number;
  referralsApproved: number;
  referralsApprovedTotal: number;
  totalEarned: string;
  pendingEarnings: string;
}

export interface ReferralItem {
  id: string;
  email: string;
  earnings: string | null;
  status: string;
  statusType: "success" | "warning" | "error" | "default";
  lastUpdated: string;
  companyName: string;
}

export interface ReferralListResponse {
  referrals: ReferralItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReferralLinkResponse {
  referralLink: string;
  message: string;
}

export interface ApiResponse<T> {
  ok: boolean;
  payload: T;
}

export interface ReferralDataResponse {
  stats: ReferralStats;
  referrals: ReferralItem[];
  total: number;
  page: number;
  pageSize: number;
}

const getCustomerId = (): string => {
  try {
    const loggedInUser = localStorage.getItem("loggedInUser");
    if (loggedInUser) {
      const user = JSON.parse(loggedInUser);
      return user?.customerId || "";
    }
  } catch (e) {
    console.error("Error parsing loggedInUser", e);
  }
  return "";
};

const formatCurrency = (amount: number): string => {
  return amount > 0 ? `$${amount.toLocaleString()}` : "$0";
};

const getStatusType = (status: string): "success" | "warning" | "error" | "default" => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes("approved") || statusLower.includes("qualified") || statusLower.includes("reward")) {
    return "success";
  }
  if (statusLower.includes("pending") || statusLower.includes("progress")) {
    return "warning";
  }
  if (statusLower.includes("denied") || statusLower.includes("failed")) {
    return "error";
  }
  return "default";
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const getReferralEnrollmentStatus = async (): Promise<
  ApiResponse<{ isEnrolled: boolean; memberData?: ReferralRockMember }>
> => {
  const customerId = getCustomerId();
  if (!customerId) {
    return { ok: true, payload: { isEnrolled: false } };
  }

  const requestPath = `${baseUrl}/customer/${customerId}/referralrock-member`;
  const response = await getRequest(requestPath, false);

  if (!response.ok) {
    return {
      ok: true,
      payload: { isEnrolled: false },
    };
  }

  const memberData: ReferralRockMember = response.payload;
  const hasRequiredFields = memberData?.id && memberData?.referralURL;

  return {
    ok: true,
    payload: {
      isEnrolled: Boolean(hasRequiredFields),
      memberData: hasRequiredFields ? memberData : undefined,
    },
  };
};

export const joinReferralProgram = async (): Promise<
  ApiResponse<{ message: string; memberData?: ReferralRockMember }>
> => {
  const customerId = getCustomerId();
  if (!customerId) {
    return { ok: false, payload: { message: "Customer ID not found" } };
  }

  const requestPath = `${baseUrl}/customer/${customerId}/referralrock-enroll`;
  const response = await postRequest(requestPath, null, false);

  if (!response.ok) {
    return {
      ok: false,
      payload: { message: "Failed to enroll in referral program" },
    };
  }

  const enrollData: ReferralRockEnrollResponse = response.payload;

  const hasRequiredFields = enrollData?.data?.id && enrollData?.data?.referralURL;

  if (!hasRequiredFields) {
    return {
      ok: false,
      payload: { message: "Enrollment failed. Please try again." },
    };
  }

  return {
    ok: true,
    payload: {
      message: enrollData.message || "Successfully enrolled in referral program",
      memberData: enrollData.data,
    },
  };
};

export const getReferralLink = async (
  memberData?: ReferralRockMember | null
): Promise<ApiResponse<ReferralLinkResponse>> => {
  if (memberData?.referralURL) {
    return {
      ok: true,
      payload: {
        referralLink: memberData.referralURL,
        message:
          "Hiring remote talent? Edge helps you build a reliable, cost-effective team with pre-vetted professionals and end-to-end support.",
      },
    };
  }

  const customerId = getCustomerId();
  if (!customerId) {
    return {
      ok: false,
      payload: {
        referralLink: "",
        message: "",
      },
    };
  }

  const requestPath = `${baseUrl}/customer/${customerId}/referralrock-member`;
  const response = await getRequest(requestPath, false);

  if (!response.ok) {
    return {
      ok: false,
      payload: {
        referralLink: "",
        message: "",
      },
    };
  }

  const fetchedMemberData: ReferralRockMember = response.payload;
  return {
    ok: true,
    payload: {
      referralLink: fetchedMemberData.referralURL || "",
      message:
        "Hiring remote talent? Edge helps you build a reliable, cost-effective team with pre-vetted professionals and end-to-end support.",
    },
  };
};

export const getReferralStats = async (memberId?: string): Promise<ApiResponse<ReferralStats>> => {
  let actualMemberId = memberId;
  if (!actualMemberId) {
    const customerId = getCustomerId();
    if (!customerId) {
      return {
        ok: false,
        payload: {
          invitesSent: 0,
          invitesOpened: 0,
          referralLinkOpened: 0,
          referralLinkUniqueVisitors: 0,
          referralsApproved: 0,
          referralsApprovedTotal: 0,
          totalEarned: "$0",
          pendingEarnings: "$0",
        },
      };
    }

    const memberPath = `${baseUrl}/customer/${customerId}/referralrock-member`;
    const memberResponse = await getRequest(memberPath, false);

    if (!memberResponse.ok || !memberResponse.payload?.id) {
      return {
        ok: false,
        payload: {
          invitesSent: 0,
          invitesOpened: 0,
          referralLinkOpened: 0,
          referralLinkUniqueVisitors: 0,
          referralsApproved: 0,
          referralsApprovedTotal: 0,
          totalEarned: "$0",
          pendingEarnings: "$0",
        },
      };
    }

    actualMemberId = memberResponse.payload.id;
  }

  const referralsPath = `${baseUrl}/referralrock-referrals?memberId=${actualMemberId}&page=1&limit=1`;
  const referralsResponse = await getRequest(referralsPath, false);

  if (!referralsResponse.ok) {
    return {
      ok: false,
      payload: {
        invitesSent: 0,
        invitesOpened: 0,
        referralLinkOpened: 0,
        referralLinkUniqueVisitors: 0,
        referralsApproved: 0,
        referralsApprovedTotal: 0,
        totalEarned: "$0",
        pendingEarnings: "$0",
      },
    };
  }

  const referralsData: ReferralRockReferralsResponse = referralsResponse.payload;
  const { stats } = referralsData;

  return {
    ok: true,
    payload: {
      invitesSent: stats.memberShares || 0,
      invitesOpened: stats.memberViews || 0,
      referralLinkOpened: stats.memberViews || 0,
      referralLinkUniqueVisitors: stats.referralsViews || 0,
      referralsApproved: stats.referralsApproved || 0,
      referralsApprovedTotal: stats.referralsCount || 0,
      totalEarned: formatCurrency(stats.rewardsIssuedAmount || 0),
      pendingEarnings: formatCurrency(stats.rewardsPendingAmount || 0),
    },
  };
};

export const getReferralList = async (
  page = 1,
  pageSize = 10,
  memberId?: string
): Promise<ApiResponse<ReferralListResponse>> => {
  let actualMemberId = memberId;
  if (!actualMemberId) {
    const customerId = getCustomerId();
    if (!customerId) {
      return {
        ok: false,
        payload: {
          referrals: [],
          total: 0,
          page,
          pageSize,
        },
      };
    }

    const memberPath = `${baseUrl}/customer/${customerId}/referralrock-member`;
    const memberResponse = await getRequest(memberPath, false);

    if (!memberResponse.ok || !memberResponse.payload?.id) {
      return {
        ok: false,
        payload: {
          referrals: [],
          total: 0,
          page,
          pageSize,
        },
      };
    }

    actualMemberId = memberResponse.payload.id;
  }

  const referralsPath = `${baseUrl}/referralrock-referrals?memberId=${actualMemberId}&page=${page}&limit=${pageSize}`;
  const referralsResponse = await getRequest(referralsPath, false);

  if (!referralsResponse.ok) {
    return {
      ok: false,
      payload: {
        referrals: [],
        total: 0,
        page,
        pageSize,
      },
    };
  }

  const referralsData: ReferralRockReferralsResponse = referralsResponse.payload;

  const referrals: ReferralItem[] = referralsData.referrals.map((ref) => ({
    id: ref.id,
    email: ref.email,
    earnings: ref.amount > 0 ? formatCurrency(ref.amount) : null,
    status: ref.status,
    statusType: getStatusType(ref.status),
    lastUpdated: formatDate(ref.updateDate),
    companyName: ref.customText2Value || ref.companyName || "-",
  }));

  return {
    ok: true,
    payload: {
      referrals,
      total: referralsData.pagination.totalCount,
      page: referralsData.pagination.page,
      pageSize: referralsData.pagination.limit,
    },
  };
};

export const getReferralData = async (
  page = 1,
  pageSize = 10,
  memberId: string
): Promise<ApiResponse<ReferralDataResponse>> => {
  if (!memberId) {
    return {
      ok: false,
      payload: {
        stats: {
          invitesSent: 0,
          invitesOpened: 0,
          referralLinkOpened: 0,
          referralLinkUniqueVisitors: 0,
          referralsApproved: 0,
          referralsApprovedTotal: 0,
          totalEarned: "$0",
          pendingEarnings: "$0",
        },
        referrals: [],
        total: 0,
        page,
        pageSize,
      },
    };
  }

  const referralsPath = `${baseUrl}/referralrock-referrals?memberId=${memberId}&page=${page}&limit=${pageSize}`;
  const referralsResponse = await getRequest(referralsPath, false);

  if (!referralsResponse.ok) {
    return {
      ok: false,
      payload: {
        stats: {
          invitesSent: 0,
          invitesOpened: 0,
          referralLinkOpened: 0,
          referralLinkUniqueVisitors: 0,
          referralsApproved: 0,
          referralsApprovedTotal: 0,
          totalEarned: "$0",
          pendingEarnings: "$0",
        },
        referrals: [],
        total: 0,
        page,
        pageSize,
      },
    };
  }

  const referralsData: ReferralRockReferralsResponse = referralsResponse.payload;
  const { stats } = referralsData;

  const referrals: ReferralItem[] = referralsData.referrals.map((ref) => ({
    id: ref.id,
    email: ref.email,
    earnings: ref.amount > 0 ? formatCurrency(ref.amount) : null,
    status: ref.status,
    statusType: getStatusType(ref.status),
    lastUpdated: formatDate(ref.updateDate),
    companyName: ref.customText2Value || ref.companyName || "-",
  }));

  return {
    ok: true,
    payload: {
      stats: {
        invitesSent: stats.memberShares || 0,
        invitesOpened: stats.memberViews || 0,
        referralLinkOpened: stats.memberViews || 0,
        referralLinkUniqueVisitors: stats.referralsViews || 0,
        referralsApproved: stats.referralsApproved || 0,
        referralsApprovedTotal: stats.referralsCount || 0,
        totalEarned: formatCurrency(stats.rewardsIssuedAmount || 0),
        pendingEarnings: formatCurrency(stats.rewardsPendingAmount || 0),
      },
      referrals,
      total: referralsData.pagination.totalCount,
      page: referralsData.pagination.page,
      pageSize: referralsData.pagination.limit,
    },
  };
};

export const sendReferralInvites = async (
  emails: string[]
): Promise<ApiResponse<{ message: string; invitesSent: number }>> => {
  const customerId = getCustomerId();
  if (!customerId) {
    return {
      ok: false,
      payload: {
        message: "Customer ID not found",
        invitesSent: 0,
      },
    };
  }

  const requestPath = `${baseUrl}/customer/${customerId}/referralrock-invite`;
  const response = await postRequest(requestPath, JSON.stringify({ emails }), false);

  if (!response.ok) {
    return {
      ok: false,
      payload: {
        message: "Failed to send invites",
        invitesSent: 0,
      },
    };
  }

  return {
    ok: true,
    payload: {
      message: response.payload?.message || "Invites sent successfully",
      invitesSent: emails.length,
    },
  };
};
